/**
 * 3D drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const p3dDrawingsPack: ToilePack = {
    slug: '3d-drawings',
    category: 'illustration',
    name: '3D drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'hole', label: 'Hole in paper', subject: 'a hole that seems to open in the sheet of paper, a ladder going down into the dark inside'},
        {key: 'box', label: 'Standing box', subject: 'a cube that seems to stand on the paper, its cast shadow falling across the sheet'},
        {key: 'drop', label: 'Water drop', subject: 'a single water drop that seems to rest on the page, its highlight and soft shadow drawn'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: "graphite pencil trompe-l'oeil drawing, hard cast shadow and smooth gradients that make the form lift off the sheet", palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: "pen and ink trompe-l'oeil drawing, dense hatching for depth and a crisp shadow edge", palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: "charcoal trompe-l'oeil drawing, deep velvety darks and a bright lifted highlight for a strong illusion of depth", palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['a staircase', 'a pit', 'a sphere'],
    directionHint: 'try: a staircase',
    seo: {
        title: '3D Drawings: Hole in the Page, Standing Box, Water Drop',
        metaDescription: 'Nine 3D illusion drawings: a hole with a ladder, a box standing on the page and a water drop on the paper, each drawn in three media.',
        h1: '3D drawings: a hole in the page, a standing box, a water drop',
        tagline: "Three trompe-l'oeil drawings, each in graphite pencil, pen and ink, and charcoal.",
        intro: 'Three drawings that seem to leave the flat sheet: a hole that opens in the paper with a ladder going down, a cube standing on the page, and a water drop resting on the paper. Each is drawn in pencil, pen and ink, and charcoal, with a hard cast shadow and smooth gradients. Type a different object or a twist, such as a staircase, a pit or a sphere, and the same media carry it.',
        useCases: [
            'a trick drawing to show a class',
            'a sketchbook exercise in shadow and depth',
            'a poster that seems to stand off the wall',
            'a gift drawing for a friend who draws',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three illusions: a hole with a ladder, a standing box, and a water drop. Each illusion appears in three media.'},
            {q: 'Can I ask for a different 3D drawing?', a: 'Yes. Type an object or twist, such as a staircase, a pit or a sphere. A typed direction is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Pencil uses graphite with smooth gradients and a hard cast shadow. Pen and ink uses dense hatching for depth. Charcoal uses deep darks and a bright lifted highlight.'},
            {q: 'Is each drawing square?', a: 'Yes. The 3D drawings are square.'},
        ],
    },
}
